import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { environment } from '@app-environment';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { ThemeService } from '../../root-services/theme.service';
import { EThemeType } from '../../enums/theme-type.enum';

@Component({
    selector: 'app-header-actions',
    templateUrl: './header-actions.component.html',
    styleUrls: ['./header-actions.component.scss'],
    standalone: false
})
export class HeaderActionsComponent implements OnInit, OnDestroy {
    private translateService = inject(TranslateService);
    private theme = inject(ThemeService);

    public selectedLanguage = this.translateService.currentLang() ?? 'en';
    public readonly standardVivaEngageLink =
        'https://engage.cloud.microsoft/main/org/siemens.com/groups/eyJfdHlwZSI6Ikdyb3VwIiwiaWQiOiIxNzMzMTA2MiJ9/all';
    public readonly _standardPaperGuidlinesLink = {
        en: 'https://siemens.sharepoint.com/teams/Siemens-Stationery/en/SitePages/Corporate-Design-Guidelines-for-Siemens-Stationery.aspx#',
        de: 'https://siemens.sharepoint.com/teams/Siemens-Stationery/de/SitePages/Corporate-Design-Guidelines-for-Siemens-Stationery.aspx#'
    };
    public readonly myLearningWorldLink =
        'https://mylearningworld.siemens.com/web/de/app/knowledge-board/lex_auth_013991160297979904379';
    public readonly scfTemplatesBucketUrl = environment.scfTemplatesBucketUrl;

    private readonly scfTeamIntranetPageUrlEn =
        'https://siemens.sharepoint.com/teams/GMS/en/csfServices/SitePages/Home.aspx?referrer=Yammer&referrerScenario=Feed.View';
    private readonly scfTeamIntranetPageUrlDe =
        'https://siemens.sharepoint.com/teams/GMS/de/csfServices/SitePages/Home.aspx?referrer=Yammer&referrerScenario=Feed.View';

    public scfHelpUrl = `${this.scfTemplatesBucketUrl}/help/SCF-Help.pdf`;
    public scfQuickStartUrl = `${this.scfTemplatesBucketUrl}/help/SCF-Schnell.pdf`;
    public logoSrc = '/assets/images/scf-logo.png';
    private destroy$: Subject<boolean> = new Subject<boolean>();

    get standardPaperGuidlinesLink() {
        return this._standardPaperGuidlinesLink?.[this.selectedLanguage] ?? this._standardPaperGuidlinesLink.en;
    }

    get themeLinks() {
        return this.theme.activeTheme?.links;
    }

    get isSiemensTheme(): boolean {
        return this.theme.activeTheme?.cssClass === EThemeType.SIEMENS;
    }

    get scfTeamIntranetPageUrl(): string {
        return this.selectedLanguage === 'en' ? this.scfTeamIntranetPageUrlEn : this.scfTeamIntranetPageUrlDe;
    }

    ngOnInit() {
        this.translateService.onLangChange.subscribe((event: LangChangeEvent) => (this.selectedLanguage = event.lang));
        this.theme.activeTheme$
            .pipe(
                tap((theme) => {
                    this.setUrl(theme.cssClass);
                }),
                takeUntil(this.destroy$)
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    private setUrl(cssClass: string): void {
        let helpFile = 'SCF-Help.pdf';
        let quickStartFile = 'SCF-Schnell.pdf';
        switch (cssClass) {
            case 'energy':
                helpFile = 'SCF-Help-SE.pdf';
                quickStartFile = 'SCF-Schnell-SE.pdf';
                break;
            case 'healthineers':
                helpFile = 'SCF-Help-SHS.pdf';
                quickStartFile = 'SCF-Schnell-SHS.pdf';
                break;
            default: // siemens and all other cases
                helpFile = 'SCF-Help.pdf';
                quickStartFile = 'SCF-Schnell.pdf';
                break;
        }

        this.scfHelpUrl = `${this.scfTemplatesBucketUrl}/help/${helpFile}`;
        this.scfQuickStartUrl = `${this.scfTemplatesBucketUrl}/help/${quickStartFile}`;
    }

    public changeLanguage(language: string) {
        this.translateService.use(language);
    }
}
